import { AlertTriangle, ChevronDown, Clock, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CheckCircleIcon from '@/assets/icons/check-circle.svg';
import XCircleIcon from '@/assets/icons/x-circle.svg';
import { Separator } from '@/components/ui/separator';
import { TruncatedText } from '@/components/ui/truncated-text';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/utils/classnames';

import { ChannelStatusRow } from './channel-status-row';

import type { OpeningResult } from '../types';
import type { useRetryPostingItem } from '../use-posting-batch';

interface OpeningResultCardProps {
  opening: OpeningResult;
  /** Read on mount only, so a card the reader toggled keeps its state. */
  defaultExpanded?: boolean;
  /** A posting batch's Retry, handed to every row. */
  retry?: ReturnType<typeof useRetryPostingItem>;
}

export function OpeningResultCard({
  opening,
  defaultExpanded = false,
  retry,
}: OpeningResultCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const intl = useIntl();

  const count = (status: OpeningResult['channels'][number]['status']) =>
    opening.channels.filter(c => c.status === status).length;
  const postingCount = count('posting');
  const queuedCount = count('queued');
  const failedCount = count('failed');

  // A running row outranks a settled outcome until the opening settles.
  const isPosting = postingCount > 0;
  const isQueued = !isPosting && queuedCount > 0;
  const isSettled = !isPosting && !isQueued;
  const allPosted = isSettled && failedCount === 0;
  const allFailed = isSettled && failedCount === opening.channels.length;

  return (
    <li
      className={cn(
        'bg-fx-neutral-50 border-fx-neutral-100 flex flex-col gap-3 rounded-lg border',
        isExpanded && 'pb-3',
      )}
    >
      <button
        type="button"
        className={cn(
          'hover:bg-fx-neutral-100 flex items-center justify-between gap-2 rounded-lg p-3',
          isExpanded && '-mb-3 rounded-b-none',
        )}
        onClick={() => setIsExpanded(prev => !prev)}
      >
        <TruncatedText variant="small1" as="span">
          {opening.openingTitle}
        </TruncatedText>

        <div className="flex shrink-0 items-center gap-2">
          {isPosting && (
            <div className="flex items-center gap-1">
              <Loader2 className="text-fx-primary size-4 animate-spin" />
              <Typography variant="small1" className="text-fx-neutral-600">
                <FormattedMessage
                  defaultMessage="Posting"
                  description="Badge shown while a channel for an opening is posting"
                />
              </Typography>
            </div>
          )}

          {/* Darker than the design's neutral-400, which fails AA text contrast here. */}
          {isQueued && (
            <div className="flex items-center gap-1">
              <Clock className="text-fx-neutral-500 size-4" />
              <Typography
                variant="small1"
                className="text-fx-neutral-600 italic"
              >
                <FormattedMessage
                  defaultMessage="Queued"
                  description="Badge shown while an opening's channels wait to post"
                />
              </Typography>
            </div>
          )}

          {allPosted && (
            <div className="flex items-center gap-1">
              <img src={CheckCircleIcon} alt="" />
              <Typography variant="small1" className="text-fx-green-800">
                <FormattedMessage
                  defaultMessage="All posted"
                  description="Badge shown when all channels for an opening posted successfully"
                />
              </Typography>
            </div>
          )}

          {allFailed && (
            <div className="flex items-center gap-1">
              <img src={XCircleIcon} alt="" />
              <Typography variant="small1" className="text-fx-rose">
                <FormattedMessage
                  defaultMessage="All failed"
                  description="Badge shown when all channels for an opening failed to post"
                />
              </Typography>
            </div>
          )}

          {isSettled && !allPosted && !allFailed && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="text-fx-rose size-3" />
              <Typography variant="small1" className="text-fx-rose">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'Failed for {count} {count, plural, one {channel} other {channels}}',
                    description:
                      'Badge shown when some channels failed for an opening',
                  },
                  { count: failedCount },
                )}
              </Typography>
            </div>
          )}

          <ChevronDown
            className={cn(
              'size-4 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        </div>
      </button>

      {isExpanded && (
        <>
          <Separator />
          <ul className="flex flex-col gap-3">
            {opening.channels.map(channel => (
              <ChannelStatusRow
                key={
                  channel.itemId ??
                  `${opening.openingId}-${channel.channelSlug}`
                }
                channel={channel}
                {...(retry && { retry })}
              />
            ))}
          </ul>
        </>
      )}
    </li>
  );
}
